import { useEffect } from 'react';

import { refetchToken, redirectToLogin, type RefreshedSession } from '@shared/auth/auth';
import { AuthError, AUTH_ERROR_CODE } from '@shared/auth/errors';
import { isTokenValid, shouldRefreshToken } from '@shared/auth/token.store';

export function useTokenRefresh(onRefreshed?: (session: RefreshedSession) => void): void {
  useEffect(() => {
    const check = async () => {
      if (!isTokenValid()) {
        try {
          onRefreshed?.(await refetchToken());
        } catch (err) {
          // Only redirect on a definitive auth rejection (401/403).
          // Network outages or 5xx responses should not log the user out;
          // AuthProvider.useEffect handles the initial silent-refresh redirect.
          const isAuthError =
            err instanceof AuthError && err.code === AUTH_ERROR_CODE.UNAUTHENTICATED;
          if (isAuthError) redirectToLogin();
        }
      } else if (shouldRefreshToken()) {
        try {
          onRefreshed?.(await refetchToken());
        } catch {
          // silent — token still valid, don't redirect
        }
      }
    };

    void check();
    const id = setInterval(() => void check(), 60_000);
    return () => {
      clearInterval(id);
    };
  }, [onRefreshed]);
}
